/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * The established five-item mobile navigation and friend-taxonomy switcher.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import CommunityNavBadge from "./CommunityNavBadge";
import SearchModal from "./SearchModal";
import TaxonomyCollectionSwitcher from "./TaxonomyCollectionSwitcher";

function ExploreIcon({ name }) {
  const props = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  switch (name) {
    case "home":
      return <svg {...props}><path d="M3 10.8 12 3l9 7.8V21h-6v-7H9v7H3Z" /></svg>;
    case "explore":
      return <svg {...props}><circle cx="12" cy="12" r="9" /><path d="m15.9 8.1-2.2 5.5-5.5 2.2 2.2-5.5Z" /></svg>;
    case "camera":
      return (
        <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 7h3l2-2h6l2 2h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" />
              <circle cx="12" cy="13" r="3.4" />
            </svg>
      );
    case "profile":
      return <svg {...props}><circle cx="12" cy="7.5" r="4" /><path d="M4.5 21v-2.7a7.5 7.5 0 0 1 15 0V21" /></svg>;
    default:
      return <svg {...props}><circle cx="9" cy="8" r="3" /><path d="M2.5 20v-2a6.5 6.5 0 0 1 13 0v2M17 5.5a3 3 0 0 1 0 5.5M17 14a5.5 5.5 0 0 1 4 5.5" /></svg>;
  }
}

export default function BioIndexExploreNavigation({ uid }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [collectionPickerOpen, setCollectionPickerOpen] = useState(false);
  const [visitedProfileName, setVisitedProfileName] = useState("");
  const containerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const menuId = "bioindex-explore-collection-menu";
  const isMyCollection = !uid || uid === auth.currentUser?.uid;
  const searchLabel = isMyCollection
    ? "Search My BioIndex"
    : visitedProfileName
      ? `Search ${visitedProfileName}'s BioIndex`
      : "Search This BioIndex";

  useEffect(() => {
    let cancelled = false;

    if (isMyCollection) {
      setVisitedProfileName("");
      return undefined;
    }

    getDoc(doc(db, "profile", uid))
      .then((snapshot) => {
        if (!cancelled) {
          setVisitedProfileName(String(snapshot.data()?.name || "").trim());
        }
      })
      .catch(() => {
        if (!cancelled) setVisitedProfileName("");
      });

    return () => {
      cancelled = true;
    };
  }, [uid, isMyCollection]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const closeOutside = (event) => {
      // On mobile the backdrop already closes the dialog. Avoid closing it
      // during pointerdown inside a collection dropdown rendered in a portal.
      if (window.matchMedia("(max-width: 639px)").matches) {
        if (!mobileMenuRef.current?.contains(event.target)) setMenuOpen(false);
        return;
      }
      if (!containerRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen]);

  function openSearch() {
    setMenuOpen(false);
    setSearchOpen(true);
  }
  function openVoiceSearch() {
    setMenuOpen(false);
    setVoiceOpen(true);
  }
  function openCollectionPicker() {
    setMenuOpen(false);
    setCollectionPickerOpen(true);
  }

  return (
    <div className="bioindex-explore-navigation-root" ref={containerRef}>
      <div className="bioindex-explore-desktop-nav">
        <div className="bioindex-explore-navbar-inner">
          <Link to="/" aria-label="BioIndex Home" className="bioindex-explore-brand">
            <img src="/favicon.ico" alt="" />
            <span className="bioindex-explore-brand-wordmark">
              <strong>BioIndex</strong>
              <small>Explore Taxonomy</small>
            </span>
          </Link>
          <span className="bioindex-explore-brand-divider" aria-hidden="true" />
          <nav aria-label="Primary navigation" className="bioindex-explore-navbar-links">
            <Link to="/">Home</Link>
            <button
              type="button"
              className="bioindex-explore-nav-active bioindex-explore-trigger"
              onClick={() => setMenuOpen((open) => !open)}
              aria-haspopup="true"
              aria-expanded={menuOpen}
              aria-controls={menuId}
            >
              Explore Taxonomy
              <span className="bioindex-explore-chevron" aria-hidden="true">⌄</span>
            </button>
            <Link to="/upload">Add Photos</Link>
            <Link to="/profile">Profile</Link>
            <Link to="/forum" className="bioindex-explore-community-link">
              Community
              <CommunityNavBadge />
            </Link>
            <button type="button" onClick={openSearch}>Search</button>
          </nav>
        </div>
      </div>

      <nav className="bioindex-m-bottom-bar bioindex-explore-mobile-nav" aria-label="Primary navigation">
        <div className="bioindex-m-bottom-grid">
          <Link className="bioindex-m-bottom-item" to="/" aria-label="Home">
            <ExploreIcon name="home" />
            <span>Home</span>
          </Link>
          <div className="bioindex-m-explore-wrap" ref={mobileMenuRef}>
          <button
            type="button"
            className="bioindex-m-bottom-item bioindex-m-explore-trigger bioindex-m-current bioindex-explore-trigger"
            onClick={() => setMenuOpen((open) => !open)}
            aria-haspopup="true"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            aria-label="Explore: search or switch collections"
          >
            <ExploreIcon name="explore" />
            <span>Explore</span>
          </button>
            {menuOpen && (
              <div className="bioindex-m-explore-menu" role="group" aria-label="Explore taxonomy options">
                <button type="button" onClick={openSearch} className="bioindex-m-explore-choice">
                  <span className="bioindex-m-explore-choice-icon" aria-hidden="true"><img src="/Search.png" alt="" /></span>
                  <span>{searchLabel}</span>
                </button>
                <button type="button" onClick={openCollectionPicker} className="bioindex-m-explore-choice">
                  <span className="bioindex-m-explore-choice-icon" aria-hidden="true"><ExploreIcon name="explore" /></span>
                  <span>Switch collection</span>
                </button>
                <button type="button" onClick={openVoiceSearch} className="bioindex-m-explore-choice">
                  <span className="bioindex-m-explore-choice-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8" /></svg></span>
                  <span>Voice Search</span>
                </button>
              </div>
            )}
          </div>
          <Link className="bioindex-m-bottom-item bioindex-m-add" to="/upload" aria-label="Add Photos">
            <span className="bioindex-m-add-circle" aria-hidden="true"><ExploreIcon name="camera" /></span>
            <span>Add Photos</span>
          </Link>
          <Link className="bioindex-m-bottom-item" to="/profile" aria-label="Profile">
            <ExploreIcon name="profile" />
            <span>Profile</span>
          </Link>
          <Link className="bioindex-m-bottom-item" to="/forum" aria-label="Community">
            <span className="bioindex-m-current-icon">
              <ExploreIcon name="community" />
              <CommunityNavBadge />
            </span>
            <span>Community</span>
          </Link>
        </div>
      </nav>

      {menuOpen && (
        <>
          <button
            type="button"
            className="bioindex-explore-menu-scrim bioindex-explore-desktop-menu-scrim"
            aria-label="Close Explore menu"
            onClick={() => setMenuOpen(false)}
          />
          <div
            id={menuId}
            className="bioindex-explore-collection-menu bioindex-explore-desktop-collection-menu"
            role="dialog"
            aria-label="Explore taxonomy options"
          >
            <div className="bioindex-explore-menu-header">
              <h2 className="bioindex-explore-menu-heading">
                <span className="bioindex-explore-desktop-menu-title">Switch collection</span>
                <span className="bioindex-explore-mobile-menu-title">Explore taxonomy</span>
              </h2>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="bioindex-explore-menu-close"
                aria-label="Close Explore menu"
              >
                ×
              </button>
            </div>

            {/* Desktop has Search in the main navigation. This action is mobile-only. */}
            <button
              type="button"
              onClick={openSearch}
              className="bioindex-explore-menu-search bioindex-explore-mobile-search"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="10.8" cy="10.8" r="6.8" />
                <path d="m16 16 4.6 4.6" />
              </svg>
              <span>{searchLabel}</span>
              <span className="bioindex-explore-action-chevron" aria-hidden="true">›</span>
            </button>

            <div className="bioindex-explore-menu-collection-label">Switch collection</div>
            <div className="bioindex-explore-switcher-wrapper">
              {/* Preserve the established friend collection switching and routing. */}
              <TaxonomyCollectionSwitcher uid={uid} />
            </div>
          </div>
        </>
      )}

      {collectionPickerOpen && typeof document !== "undefined" && createPortal(
        <TaxonomyCollectionSwitcher uid={uid} variant="dialog" onClose={() => setCollectionPickerOpen(false)} />,
        document.body,
      )}
      {voiceOpen && typeof document !== "undefined" && createPortal(
        <SearchModal open voiceOnly uid={uid} onClose={() => setVoiceOpen(false)} />,
        document.body,
      )}

      {searchOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="bioindex-explore-search-portal">
            <SearchModal
              open={true}
              onClose={() => setSearchOpen(false)}
              uid={uid}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
